import { Badge } from "@/components/ui/badge";

interface NetworkData {
  rsrp?: number;
  rsrq?: number;
  snr?: number;
  rssi?: number;
  csq?: number;
}

interface NetworkQualityBadgeProps {
  network: NetworkData;
}

export function NetworkQualityBadge({ network }: NetworkQualityBadgeProps) {
  const calculateQualityScore = (network: NetworkData): number => {
    let score = 0;

    // RSRP contribution (0-60 points)
    if (network.rsrp !== undefined) {
      const rsrpScore = Math.max(0, Math.min(60, ((network.rsrp + 120) / 40) * 60));
      score += rsrpScore;
    }

    // RSRQ contribution (0-25 points)
    if (network.rsrq !== undefined) {
      const rsrqScore = Math.max(0, Math.min(25, ((network.rsrq + 15) / 8) * 25));
      score += rsrqScore;
    }

    // SNR contribution (0-15 points)
    if (network.snr !== undefined) {
      const snrScore = Math.max(0, Math.min(15, (network.snr / 20) * 15));
      score += snrScore;
    }
    // Fallback to RSSI if LTE metrics not available
    else if (network.rssi !== undefined) {
      const rssiScore = Math.max(0, Math.min(40, ((network.rssi + 110) / 40) * 40));
      score += rssiScore;
    }
    // Final fallback to CSQ
    else if (network.csq !== undefined) {
      const csqScore = Math.max(0, Math.min(40, ((network.csq - 5) / 20) * 40));
      score += csqScore;
    }

    return Math.round(Math.max(0, Math.min(100, score)));
  };

  const getQualityVariant = (score: number) => {
    if (score >= 80) return { variant: "default" as const, label: "Excellent", className: "bg-green-400 hover:bg-green-500 text-white border-green-400 text-center justify-center" };
    if (score >= 60) return { variant: "secondary" as const, label: "Good", className: "bg-blue-400 hover:bg-blue-500 text-white border-blue-400 text-center justify-center" };
    if (score >= 35) return { variant: "secondary" as const, label: "Fair", className: "bg-yellow-400 hover:bg-yellow-500 text-white border-yellow-400 text-center justify-center" };
    return { variant: "destructive" as const, label: "Poor", className: "bg-red-400 hover:bg-red-500 text-white border-red-400 text-center justify-center" };
  };

  const score = calculateQualityScore(network);
  const quality = getQualityVariant(score);

  if (score === 0) {
    return <Badge variant="outline">No Data</Badge>;
  }

  return (
    <div className="flex items-center gap-2">
      <Badge 
        variant={quality.variant} 
        className={quality.className}
      >
        {quality.label}
      </Badge>
      <span className="text-sm font-mono text-muted-foreground">{score}%</span>
    </div>
  );
}